import { Card } from "./ui/card"
import { useEffect, useState } from "react"
import { CustomToggle } from "./CustomToggle"
import { AuthModel } from "../models/auth.model"
import { AuthService } from "../services/auth.service"
import { formatTimeDifference, getCurrentDate } from "../lib/utils"
import { ChartBar, RefreshCcw } from "lucide-react"

export interface LocationReportProps {
    id: number,
    globalDateOffset: number,
    timezone: string,
    changeDate: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}

export function LocationReport(props: LocationReportProps) {
    const [dateOffset, setDateOffset] = useState<number>(0)
    const [raw, setRaw] = useState<boolean>(false)
    const [loadedOn, setLoadedOn] = useState<string>(new Date().toISOString())

    const [currentTime, setCurrentTime] = useState(new Date())

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date())
        }, 1000)

        return () => clearInterval(interval)
    }, [])

    useEffect(() => {
        setLoadedOn(new Date().toISOString())
    }, [raw, dateOffset, props.globalDateOffset])

    function getLocationMap(requiredDateOffset: number): string {
        const auth: AuthModel = AuthService.getAuth()
        const date = getCurrentDate(props.globalDateOffset, props.timezone)
        const offset = getCurrentDate(requiredDateOffset + props.globalDateOffset, props.timezone)
        const base = 'https://eluxnetworks.net/function/well-api/api?name=get_full_location_map'
        return `${base}&deployment_id=${props.id}&date=${date}&re_create=false&bw=false&motion=false&scale_global=false&map_type=7&user_name=${auth.username}&fast=true&filter=5&token=${auth.token}&to_date=${offset}&raw=${raw}&now=${loadedOn}`
    }

    function toggleRawLocationMap(checked: boolean) {
        setRaw(checked)
        setDateOffset(dateOffset)
    }

    function generateAcitvityReportButtonClassNames(active: boolean = false) {
        return active ? 'px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg' : 'px-4 py-2 text-sm font-medium bg-gray-200 rounded-lg hover:bg-gray-300'
    }


    return (
        <Card className="p-4 space-y-4 shadow-md rounded-lg border" id="location_report">
            <div className="space-y-2">
                <div className="flex justify-between items-center border-b pb-2">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                    <ChartBar className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg text-3xl" size={32}/> Location Report
                    </h3>
                    <select
                        className="flex-grow w-full max-w-[45%] px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-800 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                        value={props.globalDateOffset}
                        onChange={props.changeDate}
                    >
                        {Array.from({ length: 30 }, (_, i) => {
                            const offset = -1 * i;
                            return (
                                <option key={i} value={offset}>
                                    {offset === 0 ? 'Now' : getCurrentDate(offset, props.timezone)}
                                </option>
                            );
                        })}
                    </select>
                </div>
                <div className="text-sm flex justify-center items-center gap-2">
                    <p>
                        {(() => {
                            const calculated = formatTimeDifference(loadedOn)
                            return `Last report ${calculated.minutes} min ago`
                        })()}
                    </p>
                    <RefreshCcw
                        className="cursor-pointer"
                        onClick={() => {
                            setDateOffset(dateOffset - 1) // Temporarily change
                            setTimeout(() => setDateOffset(dateOffset), 0) // Reset immediately
                        }}
                    />
                </div>

                <div className="flex justify-center gap-2 mt-2">
                    <button className={generateAcitvityReportButtonClassNames(dateOffset === 0)} type="button" onClick={(e) => setDateOffset(0)}>
                        Day
                    </button>
                    <button className={generateAcitvityReportButtonClassNames(dateOffset === -7)} type="button" onClick={(e) => setDateOffset(-7)}>
                        Week
                    </button>
                    <button className={generateAcitvityReportButtonClassNames(dateOffset === -30)} type="button" onClick={(e) => setDateOffset(-30)}>
                        1Mo
                    </button>
                    <button className={generateAcitvityReportButtonClassNames(dateOffset === -180)} type="button" onClick={(e) => setDateOffset(-180)}>
                        6Mo
                    </button>
                </div>
                {dateOffset === 0 &&
                    <div className="flex justify-center gap-2 items-center mt-4">
                        <span className="text-sm font-medium text-gray-800">Show raw</span>
                        <CustomToggle checked={raw} onCheckedChange={(checked) => toggleRawLocationMap(checked)} />
                    </div>
                }
                <div className="mt-4">
                    <img src={getLocationMap(dateOffset)} alt="Activity Report" className="w-full rounded-lg" />
                </div>
            </div>
        </Card>
    )
}